// DS: TA1.1
import React, { lazy, Suspense } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  PapiBadge,
  PapiUser,
  PapiDeviation,
  PapiEditorText,
} from '@wix/da-papi-types';
import {
  ThemeOverride,
  ThemeSurface,
} from '@wix/da-react-context/pkg/ThemeContext';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';
import { isEditorTextEmpty } from '@wix/da-shared-react/pkg/DaEditorWrapper/utils';

import { formatDateOfBirth } from './utils';
import MutualConnections from './MutualConnections';
import ArtistInfoLine from './ArtistInfoLine';
import Badges from './Badges';
import UserInfoBullets from './UserInfoBullets';
import CollapsibleBio from './CollapsibleBio';
import SocialMediaList from './SocialMediaList';
import { AboutMeMode } from './types';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';

const Mudkips = lazy(() => import('./MyMudkips'));

import s from './AboutMe.scss';

export interface Props {
  moduleId: number;
  moduleVersion: number;
  isEmpty?: boolean;
  user: PapiUser;
  gender?: string;
  isOwner: boolean;
  isArtist?: boolean;
  isOpenForCommissions?: boolean;
  hasActiveCommissions?: boolean;
  level?: number;
  specialty?: number;
  website?: string;
  websiteLabel?: string;
  dobDay?: number;
  dobMonth?: number;
  dobYear?: number;
  countryId?: number;
  countrycode?: string;
  deviantFor: number; // duration in seconds
  devidDeviation?: PapiDeviation;
  bio?: PapiEditorText;
  badges?: PapiBadge[];
  showBadges: boolean;
  isMobile?: boolean;
  interests?: {
    id: number;
    value: string;
  }[];
  socialLinks?: {
    id: number;
    value?: string;
  }[];
  onStopEditing: () => void;
  hasJoinedWithEclipse?: boolean;
  mutualWatchedUsers?: PapiUser[];
  mutualWatchedUsersCount?: number;
  isWatchingYou?: boolean;

  mode?: AboutMeMode;
}

const Gutter: React.FC<{
  cardMode: boolean;
  children?: React.ReactNode;
}> = ({ cardMode, children }) => (
  <div className={classnames(cardMode && s['gutter'])}>{children}</div>
);
Gutter.displayName = 'Gutter';

const AboutMe: React.FC<Props> = ({
  socialLinks,
  moduleId,
  moduleVersion,
  dobYear,
  dobDay,
  dobMonth,
  countrycode,
  deviantFor,
  website,
  websiteLabel,
  level,
  specialty,
  gender,
  badges,
  showBadges,
  bio,
  isArtist,
  interests,
  user,
  mode = AboutMeMode.DEFAULT,
  isOwner,
  mutualWatchedUsers = [],
  mutualWatchedUsersCount = 0,
  isWatchingYou = false,
}) => {
  const { t } = useTranslation();
  const isCard = mode === AboutMeMode.CARD;
  const mudkips = useFlag('mudkips');
  const mounted = useIsMounted();

  return (
    <div className={classnames(s['root'], isCard && s['card'])}>
      <ThemeOverride themeSurface={ThemeSurface.SECONDARY}>
        {!isOwner && isCard && (
          <Gutter cardMode={isCard}>
            <MutualConnections
              user={user}
              isWatchingYou={isWatchingYou}
              mutual={mutualWatchedUsers}
              mutualWatchedUsersCount={mutualWatchedUsersCount}
            />
          </Gutter>
        )}
        {isArtist && (
          <Gutter cardMode={isCard}>
            <ArtistInfoLine
              level={level}
              specialty={specialty}
              className={s['info-line']}
            />
          </Gutter>
        )}
        <Gutter cardMode={isCard}>
          <UserInfoBullets
            dateOfBirth={formatDateOfBirth(dobDay, dobMonth, dobYear, t)}
            countryCode={countrycode}
            deviantFor={deviantFor}
            gender={gender}
            website={website}
            websiteLabel={websiteLabel}
            isBirthweek={user.isBirthweek}
            username={user.username}
          />
        </Gutter>

        {socialLinks && socialLinks.length > 0 && (
          <Gutter cardMode={isCard}>
            <SectionAndLabel label={t('widgets.aboutMe.followMe')}>
              <SocialMediaList socialLinks={socialLinks} user={user} />
            </SectionAndLabel>
          </Gutter>
        )}
        {showBadges && badges && (
          <Gutter cardMode={isCard}>
            <SectionAndLabel label={t('widgets.aboutMe.myBadges')}>
              <Badges
                badges={badges}
                username={user.username}
                collapseMoreThan={6}
              />
            </SectionAndLabel>
          </Gutter>
        )}

        {mounted && mudkips && (
          <Suspense fallback={null}>
            <Gutter cardMode={isCard}>
              <SectionAndLabel label={'My NFTs'}>
                <Mudkips />
              </SectionAndLabel>
            </Gutter>
          </Suspense>
        )}

        <Gutter cardMode={isCard}>
          {bio && !isEditorTextEmpty(bio) && (
            <SectionLabel>{t('widgets.aboutMe.edit.myBio')}</SectionLabel>
          )}
          <CollapsibleBio
            bio={bio}
            interests={interests}
            mode={mode}
            moduleId={moduleId}
            moduleVersion={moduleVersion}
          />
        </Gutter>
      </ThemeOverride>
    </div>
  );
};
AboutMe.displayName = 'AboutMe';

// Ideally this would be a heading, but since we don't know what level heading we need, we should should hold off.
const SectionLabel = ({ children }) => (
  <div className={s['section-label']}>{children}</div>
);

const SectionAndLabel = ({ label, children }) => (
  <div className={s['section']}>
    <SectionLabel>{label}</SectionLabel>
    {children}
  </div>
);

export default AboutMe;
